// src/components/ui/Badge.tsx — SCE Issues
// Badge pour criticité / statut / métier / sujet

import type { CSSProperties } from 'react'
import {
  A_QUALIFIER,
  type BadgeCloture,
  type Criticite,
  type Statut,
  type Metier,
  type Sujet,
} from '@/types/issue.types'
import {
  METIERS_COULEURS,
  METIERS_COULEURS_TEXTE,
  METIERS_LABELS_COURT,
} from '@/config/metiers.config'
import { STATUTS_COULEURS, type CouleursStatut } from '@/config/statuts.config'
import { DEFINITIONS_BADGES } from '@/config/badgesCloture.config'
import { SUJETS_CONFIG } from '@/config/sujets.config'

// ─── Types de props exclusifs ───

type Rien = { sujet?: never; metierDuSujet?: never }

type BadgeProps =
  | ({ criticite: Criticite; statut?: never; metier?: never; badgeCloture?: never; court?: never; libelle?: never } & Rien)
  | ({ statut: Statut; criticite?: never; metier?: never; badgeCloture?: never; court?: never; libelle?: never } & Rien)
  | ({ metier: Metier; criticite?: never; statut?: never; badgeCloture?: never; court?: never; libelle?: never } & Rien)
  | {
      /** Sujet (23/10, « Goulotte ») : contour à la couleur du métier qui corrige. */
      sujet: Sujet
      metierDuSujet: Metier
      criticite?: never
      statut?: never
      metier?: never
      badgeCloture?: never
      court?: never
      libelle?: never
    }
  | ({
      badgeCloture: BadgeCloture
      /** Libellé court (rangée dense du bureau). Le libellé complet reste en infobulle. */
      court?: boolean
      /**
       * Remplace le code affiché — sert à montrer la référence qualité
       * (« FNC-T_2026-55 ») à la place de « FNC-T ». Cf. `libelleBadge()`.
       */
      libelle?: string
      criticite?: never
      statut?: never
      metier?: never
    } & Rien)

/**
 * Badge sémantique : criticité, statut ou métier.
 * Couleurs automatiques selon le type (conforme Sartorius Traffic Light).
 */
export function Badge(props: BadgeProps): JSX.Element {
  const { label, style, titre } = resolveBadge(props)

  return (
    <span
      className="inline-flex items-center justify-center px-2.5 py-1 rounded-badge font-medium whitespace-nowrap"
      style={{
        fontSize: 'var(--font-size-tag)',
        lineHeight: 'var(--line-height-nav)',
        ...style,
      }}
      title={titre}
    >
      {label}
    </span>
  )
}

function resolveBadge(
  props: BadgeProps,
): { label: string; style: CSSProperties; titre?: string } {
  // ─── Badge de clôture (Lot 3) ───
  //
  // Contour noir sur fond blanc, sans couleur Traffic Light : il côtoie le
  // badge de criticité et celui de statut, qui portent déjà le rouge, le
  // jaune et le vert. Coloré, il aurait dit « grave » ou « réglé » alors
  // qu'il dit seulement QUEL circuit qualité a pris le relais.
  if (props.badgeCloture) {
    const def = DEFINITIONS_BADGES[props.badgeCloture]
    return {
      label: props.court ? def.court : (props.libelle ?? props.badgeCloture),
      titre: `${props.libelle ?? props.badgeCloture} — ${def.signification}`,
      style: {
        backgroundColor: 'var(--color-white)',
        color: 'var(--color-black)',
        border: '1px solid var(--color-black)',
      },
    }
  }

  // ─── Sujet (23/10) ───
  //
  // Fond blanc, contour et point à la couleur du métier : il se lit « dans »
  // le métier (Goulotte est un problème Méca), sans en être un deuxième.
  if (props.sujet) {
    return {
      label: SUJETS_CONFIG[props.sujet].libelleCourt,
      titre: `Sujet ${props.sujet} · traité par ${METIERS_LABELS_COURT[props.metierDuSujet]}`,
      style: {
        backgroundColor: 'var(--color-white)',
        color: 'var(--color-black)',
        border: `1px solid ${METIERS_COULEURS[props.metierDuSujet]}`,
      },
    }
  }

  if (props.criticite) {
    switch (props.criticite) {
      case 'Critique':
        return {
          label: 'Critique',
          style: {
            backgroundColor: 'var(--color-traffic-red)',
            color: 'var(--color-white)',
          },
        }
      case 'Majeur':
        return {
          label: 'Majeur',
          style: {
            backgroundColor: 'var(--color-traffic-yellow)',
            color: 'var(--color-black)',
          },
        }
      case 'Mineur':
        return {
          label: 'Mineur',
          style: {
            backgroundColor: 'var(--color-traffic-green)',
            color: 'var(--color-white)',
          },
        }
      // ⚠️ Cas indispensable depuis le Lot 3.5 : l'API force TOUT nouveau
      // problème à 'À qualifier'. Sans lui, le switch tombait à travers
      // jusqu'à la branche métier et lisait METIERS_LABELS_COURT[undefined] —
      // un badge vide et sans fond sur chaque déclaration récente, c'est-à-dire
      // sur le cas le plus fréquent de la liste.
      //
      // Aucune couleur Traffic Light ici : la criticité n'est pas connue, la
      // teinte ne doit donc rien affirmer. Le trait discontinu dit « en
      // attente » sans concurrencer un badge Critique voisin.
      case A_QUALIFIER:
        return {
          label: A_QUALIFIER,
          style: {
            backgroundColor: 'var(--color-white)',
            color: 'var(--color-gray-70)',
            border: '1px dashed var(--color-gray-40)',
          },
        }
    }
  }

  if (props.statut) {
    // Couleurs : config/statuts.config.ts, source unique depuis le Lot 8.
    // Un statut inconnu (valeur SharePoint inattendue) garde son libellé,
    // sans style : il ne doit JAMAIS retomber dans la branche métier plus bas,
    // qui l'afficherait en badge vide — le défaut déjà subi avec 'À qualifier'.
    const couleurs = STATUTS_COULEURS[props.statut] as CouleursStatut | undefined
    return { label: props.statut, style: couleurs?.badge ?? {} }
  }

  // metier — texte noir ou blanc selon la teinte choisie (Lot 8).
  return {
    label: METIERS_LABELS_COURT[props.metier!],
    style: {
      backgroundColor: METIERS_COULEURS[props.metier!],
      color: METIERS_COULEURS_TEXTE[props.metier!],
    },
  }
}
